// definitions.js: the words teachers read, each defined once (W4-A, 2026-10-21).
// The Teacher dashboard imports this file. claude-teacher-api bundles a
// byte-identical copy as definitions.mjs (its build copies this file and a
// test compares the bytes). definitions.cases.json is the one test table that
// both sides run. Plain JavaScript with no imports, so it runs as is in the
// browser and in Node.

const pickOf = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : null);

// ---------- stuck ----------
// A student is STUCK in a domain when the engine marked the domain stuck
// (stuckSince, or phase 'stuck') AND this stuck episode was not released.
// A release (by the teacher, or the engine's 3-day auto-release) belongs to
// one episode: its intervention row has stuckAt = the profile's stuckSince.
// A released student is retrying, not waiting, so they are not stuck. A new
// episode (a new stuckSince) makes them stuck again.
export const RELEASED_STATUSES = ['released', 'auto_released'];
export const stuckMarked = (row) => !!row && (!!row.stuckSince || row.phase === 'stuck');
export function releaseOf(interventions, userId, domainId, stuckSince) {
  return (interventions || []).find((i) => !!i && i.userId === userId
    && String(i.domainId || '') === String(domainId || '')
    && RELEASED_STATUSES.includes(i.status)
    && String(i.stuckAt || '') === String(stuckSince || '')) || null;
}
// row: one StudentDomainProfile row. -> { stuck, stuckSince, released }
export function stuckState(row, interventions, userId, domainId) {
  if (!stuckMarked(row)) return { stuck: false, stuckSince: null, released: null };
  const r = releaseOf(interventions, userId, domainId, row.stuckSince);
  if (r) return { stuck: false, stuckSince: null, released: { status: r.status, releasedAt: r.releasedAt || null } };
  return { stuck: true, stuckSince: row.stuckSince || null, released: null };
}
// A domain fact from the Home snapshot or the roster ({ stuck, released }).
export const isStuckFact = (fact) => !!fact && fact.stuck === true && !fact.released;

// ---------- started ----------
// For one practice set or unit, each assigned student is in one group:
//   finished    answered every question (and every owed walkthrough)
//   inProgress  started, not finished
//   notStarted  no answer yet
// STARTED counts everyone who answered at least one question, so it INCLUDES
// the finished students. This is the Assignments page's meaning ("12 of 50
// finished, 20 started"). Working = started minus finished.
export function workStatus({ started = false, finished = false } = {}) {
  return finished ? 'finished' : started ? 'inProgress' : 'notStarted';
}
// statuses: workStatus values, one per assigned student.
export function participationOf(statuses) {
  const list = statuses || [];
  const n = (s) => list.filter((x) => x === s).length;
  const finished = n('finished');
  const inProgress = n('inProgress');
  return { assigned: list.length, started: finished + inProgress, finished, inProgress, notStarted: n('notStarted') };
}
// Counts from the server (listHomework progress). A count that is not a whole
// number from 0 to assigned, or a started count below finished, is unknown.
export function progressCounts({ assigned = null, started = null, finished = null } = {}) {
  const ok = (v) => Number.isInteger(v) && v >= 0 && (assigned == null || v <= assigned);
  const f = ok(finished) ? finished : null;
  const s = ok(started) && (f == null || started >= f) ? started : null;
  return {
    assigned, started: s, finished: f,
    working: s != null && f != null ? s - f : null,
    notStarted: assigned != null && s != null ? assigned - s : null,
  };
}
// A unit checkpoint slot is started when it is done, marked started, or has progress.
export const slotStarted = (slot) => !!slot && (slot.done === true || !!slot.started || Number(slot.progress) > 0);

// ---------- accuracy ----------
// ACCURACY is the percent of FIRST answers that were right, as a whole
// number. With no answers there is no percent (null, never 0). A question
// answered wrong and then fixed on a retry stays a miss.
export const accuracyPct = (correct, answered) => (answered > 0 ? Math.round((correct / answered) * 100) : null);
// row: a StudentQuestionResponses row. question_attempts[0] is the first
// answer when the trail exists; is_correct holds the LATEST answer.
// -> { graded, correct, picked, at }; graded is false for a row with no answer.
export function firstAttempt(row) {
  const first = row && Array.isArray(row.question_attempts) && row.question_attempts.length ? row.question_attempts[0] : null;
  if (first && typeof first.isCorrect === 'boolean') {
    return { graded: true, correct: first.isCorrect, picked: pickOf(first.selected ?? first.typed), at: first.ts || row.answered_at || null };
  }
  if (row && typeof row.is_correct === 'boolean') {
    return { graded: true, correct: row.is_correct, picked: pickOf(row.selected_option ?? row.typed_answer), at: row.answered_at || null };
  }
  return { graded: false, correct: null, picked: null, at: (row && row.answered_at) || null };
}

// ---------- days ----------
// Days are the CLASS's days, in the class row's time zone (default
// America/Denver). "The last N days" starts at class midnight N days before
// today and runs to now. An answer at 9 pm belongs to that evening's day.
export const DEFAULT_TIME_ZONE = 'America/Denver';
const DAY_MS = 86400000;
export function safeZone(tz) {
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz || DEFAULT_TIME_ZONE }); return tz || DEFAULT_TIME_ZONE; } catch { return DEFAULT_TIME_ZONE; }
}
// The class-local calendar day (YYYY-MM-DD) of a moment.
export function localDay(ms, tz) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: safeZone(tz), year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
}
export const addDays = (day, n) => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
function zoneOffsetMs(ms, tz) {
  const parts = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(tz), hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(ms))) parts[p.type] = p.value;
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second) - Math.floor(ms / 1000) * 1000;
}
// The UTC moment of hh:mm class time on a class day.
export function localTimeMs(day, hh, mm, tz) {
  const guess = Date.parse(`${day}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00Z`);
  const first = guess - zoneOffsetMs(guess, tz);
  return guess - zoneOffsetMs(first, tz);
}
export const dayStartMs = (day, tz) => localTimeMs(day, 0, 0, tz);
export const dayEndMs = (day, tz) => dayStartMs(addDays(day, 1), tz) - 1;
// The first class day of "the last N days".
export const windowFrom = (sinceDays, tz, nowMs) => addDays(localDay(nowMs, tz), -sinceDays);
